import React from "react";
import { withScope, captureException } from "@sentry/react";
import { Link } from "react-router-dom";
import { InfoRounded, WarningRounded, ErrorRounded } from "@mui/icons-material";

import { A, P, SupportNumber } from "commons";
import { isInProgress } from "commons/src/helpers/RequestHelpers";
import {
    SERVER_ERROR_STATUS,
    FORBIDDEN_STATUS,
    LOCKED_STATUS,
    UNAUTHORIZED_STATUS
} from "commons/src/constants/statusCodeConstants";

import { logout } from "../components/DataOperations";
import { fail } from "../reducers/requestsActions";
import { ClientSupportLink } from "../routes/Login/PlatformLinks";
import { store } from "../store";

export const ErrorSupportNumber = () => <SupportNumber bold color={getErrorStyle(ERROR).color} />;

export const SelfHelpLink = () => (
    <P>
        If you are having trouble logging in, you can find additional guidance{" "}
        <A
            href={ClientSupportLink}
            rel="noopener noreferrer"
            target="_blank"
            aria-label="login instructions"
        >
            here.
        </A>
    </P>
);

export const TOO_MANY_RETRIES_MESSAGE = (
    <div>
        <P>The error has not been resolved by re-trying.</P>
        <P>
            Please contact us on <ErrorSupportNumber /> or click on the live chat link.
        </P>
    </div>
);

export const ERROR = "ERROR";
export const WARNING = "WARNING";
export const INFO = "INFO";

export const errorStyles = {
    ERROR: {
        icon: ErrorRounded,
        color: "#f44336"
    },
    WARNING: {
        icon: WarningRounded,
        color: "#FFAB00"
    },
    INFO: {
        icon: InfoRounded,
        color: "#4CAF50"
    }
};

export const getErrorStyle = severity => errorStyles[severity] || errorStyles[WARNING];

export function handleError(requestData) {
    if (requestData.isCritical) {
        logout({
            message: requestData.errorMessage,
            severity: requestData.severity,
            errorCode: requestData.errorCode
        });
    }

    if (requestData.requestName) {
        store.dispatch(fail(requestData));
    }

    logError(requestData);
}

function logError(requestData) {
    withScope(scope => {
        requestData.errorCode && scope.setTag("errorCode", requestData.errorCode);
        scope.setLevel((requestData.severity && requestData.severity.toLowerCase()) || "info");
        captureException(requestData.error);
    });
}

function getInvalidErrorMessage(isForgottenPassword, isChangePassword) {
    if (isForgottenPassword) {
        return "The username you have provided is invalid.";
    }

    if (isChangePassword) {
        return "The current password has been entered incorrectly, please try again.";
    }

    return "Your username or registration code has been entered incorrectly, please try again.";
}

export function getAuthenticationMessage({
    error,
    isForgottenPassword = false,
    isChangePassword = false
}) {
    const commonErrors = {
        unauthorized: (
            <div>
                <P>
                    Your username and/or password has been entered incorrectly. Please check your
                    login details and try again.
                </P>
                <SelfHelpLink />
            </div>
        ),
        locked: (
            <div>
                <P>
                    Your account has been locked.{" "}
                    <Link to="/forgotten" aria-label="request new registration details">
                        Click here
                    </Link>{" "}
                    to request new registration details.
                </P>
                <SelfHelpLink />
            </div>
        )
    };

    const errorList = {
        Invalid: getInvalidErrorMessage(isForgottenPassword, isChangePassword),
        Unauthorized: commonErrors.unauthorized,
        Forbidden: commonErrors.unauthorized,
        ExistingPass: "Your new password cannot be your existing password.",
        UsernamePass: "Your password cannot resemble your username.",
        RecentlyUsedPass:
            "The password you have entered has been recently used. Your new password must differ by at least one character.",
        Locked: commonErrors.locked,
        LockedOut: commonErrors.locked,
        NotClient: commonErrors.unauthorized,
        IncorrectCredentials: commonErrors.unauthorized,
        FailedRequest: (
            <P>
                Sorry but we couldn&apos;t connect to the service. Please try again, but if the
                problem persists contact our support by phone on <ErrorSupportNumber /> between
                8:30am and 5:30pm or email us at{" "}
                <A
                    bold
                    color={getErrorStyle(ERROR).color}
                    href="mailto:client.relations@nucleusfinancial.com"
                >
                    client.relations@nucleusfinancial.com
                </A>
            </P>
        ),
        ServiceUnavailable: (
            <P>
                Sorry but Nucleus Go is not available at the moment. We are aware of the issue and
                we will try to resolve it as soon as we can. In the meantime please contact our
                support at{" "}
                <A
                    bold
                    color={getErrorStyle(ERROR).color}
                    href="mailto:client.relations@nucleusfinancial.com"
                >
                    client.relations@nucleusfinancial.com
                </A>{" "}
                or call us at <ErrorSupportNumber /> if you have any questions.
            </P>
        )
    };

    if (
        error &&
        error.status &&
        (error.status === UNAUTHORIZED_STATUS ||
            error.status === FORBIDDEN_STATUS ||
            error.status === LOCKED_STATUS)
    ) {
        return errorList[error.statusText] || errorList["FailedRequest"];
    } else if (error && error.status && error.status >= SERVER_ERROR_STATUS) {
        return errorList["ServiceUnavailable"];
    } else if (error && error instanceof Array && error.length > 1) {
        const errors = error.map(e => errorList[e]).filter(Boolean);
        return errors.length ? errors : errorList["FailedRequest"];
    } else if (errorList[error]) {
        return errorList[error];
    }

    return errorList["FailedRequest"];
}

export function getFailedFetchMessage(requestName, isCritical) {
    return isCritical ? (
        <P>
            An error occurred while you were attempting to use Nucleus Go.
            <br />
            Please contact us on <ErrorSupportNumber /> or click on the live chat link.
        </P>
    ) : (
        <P>
            Sorry there was a problem retrieving your {requestName}. <br />
            Please contact us on <ErrorSupportNumber /> or click on the live chat link.
        </P>
    );
}

export function getRefetchMessageAndAction(requestStatus, refetch) {
    if (!requestStatus.errorMessage) {
        return { message: null, action: null };
    }

    const tooManyRetries = requestStatus.numberOfRetries > 2;

    const message = tooManyRetries ? TOO_MANY_RETRIES_MESSAGE : requestStatus.errorMessage;

    const action = isInProgress(requestStatus.status)
        ? { isLoading: true }
        : { isLoading: false, retry: refetch };

    return { message, action };
}

export function getFailedToUpdatePreferencesMessage() {
    return (
        <P>
            An error occurred when attempting to save your changes
            <br />
            Please try again or call us on <ErrorSupportNumber />
        </P>
    );
}

export function getFailedToFetchPreferencesMessage() {
    return (
        <>
            <P>
                An error has occurred when attempting to retrieve your preferences due to the
                service being currently unavailable.
            </P>
            <P>
                If the error persists please contact support on <ErrorSupportNumber /> or click on
                live chat.
            </P>
        </>
    );
}

export function getFailedToSetAdHocFeesMessage() {
    return (
        <>
            <P>
                An error has occurred when attempting to update your fee authorisation preference,
                please try again.
            </P>
            <P>
                If the error persists please contact support on <ErrorSupportNumber /> or click on
                live chat.
            </P>
        </>
    );
}
